import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { 
  Shield, Star, Zap, ToggleLeft, ToggleRight, Info, Lock,
  ChevronRight, Save, RefreshCw, Settings, Users, BarChart3, Crown
} from 'lucide-react';
import { smartgateAPI } from '../../api';

// ─── Helper components ─────────────────────────────────────────────────────────

const Toggle = ({ value, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    data-testid="smartgate-toggle"
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none
      ${value ? 'bg-blue-600' : 'bg-slate-200'} ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
      ${value ? 'translate-x-6' : 'translate-x-1'}`} />
  </button>
);

const FieldRow = ({ label, hint, children }) => (
  <div className="flex items-start gap-4">
    <div className="w-64 flex-shrink-0">
      <p className="text-sm font-medium text-slate-700">{label}</p>
      {hint && <p className="text-xs text-slate-400 mt-0.5">{hint}</p>}
    </div>
    <div className="flex-1">{children}</div>
  </div>
);

const NumberInput = ({ value, onChange, min = 0, max, disabled, suffix, prefix, testId }) => (
  <div className="flex items-center gap-2">
    {prefix && <span className="text-sm text-slate-500">{prefix}</span>}
    <input
      type="number"
      value={value}
      onChange={e => onChange(parseInt(e.target.value) || 0)}
      min={min}
      max={max}
      disabled={disabled}
      data-testid={testId}
      className="w-32 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
    />
    {suffix && <span className="text-sm text-slate-500">{suffix}</span>}
  </div>
);

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
    <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3">
      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
        <Icon className="w-4 h-4 text-blue-600" />
      </div>
      <h3 className="font-semibold text-slate-800">{title}</h3>
    </div>
    <div className="px-6 py-5 space-y-5">{children}</div>
  </div>
);

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function SmartGate() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planLocked, setPlanLocked] = useState(false);
  const [currentPlan, setCurrentPlan] = useState('starter');
  const [settings, setSettings] = useState({
    wallet_feature_enabled: false,
    welcome_bonus_points: 100,
    earn_points: 5,
    earn_amount: 100,
    tryon_points_cost: 10,
    moments_points_cost: 8,
    size_recommendation_points_cost: 5,
    block_when_insufficient_balance: true,
    allow_negative_balance: false,
    negative_balance_limit: 0,
    auto_credit_from_orders_enabled: false,
    order_event_source: 'shopify',
    shopify_shop_domain: '',
  });

  const load = useCallback(async () => {
    try {
      const res = await smartgateAPI.getSettings();
      const { plan_locked, current_plan, ...rest } = res.data;
      setPlanLocked(plan_locked);
      setCurrentPlan(current_plan || 'starter');
      setSettings(s => ({ ...s, ...rest }));
    } catch (e) {
      toast.error('Failed to load SmartGate settings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (planLocked) return;
    setSaving(true);
    try {
      await smartgateAPI.updateSettings(settings);
      toast.success('Settings saved successfully');
    } catch (e) {
      const msg = e.response?.data?.detail?.message || 'Failed to save settings';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const set = (key, value) => setSettings(s => ({ ...s, [key]: value }));
  const disabled = planLocked || !settings.wallet_feature_enabled;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6" data-testid="smartgate-settings-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="w-6 h-6 text-blue-600" />
            <h1 className="text-2xl font-bold text-slate-900">SmartGate</h1>
            <span className="px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-700 rounded-full">Customer Wallet</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">
            Control how customers earn and spend points on AI actions. Reward loyal buyers and manage usage.
          </p>
        </div>
        <button
          onClick={() => navigate('/partners/smartgate/wallets')}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          data-testid="smartgate-view-wallets-btn"
        >
          <Users className="w-4 h-4" />
          View Wallets
        </button>
      </div>

      {/* Plan gate banner */}
      {planLocked && (
        <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-xl" data-testid="smartgate-plan-locked-banner">
          <Lock className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold text-amber-800">Professional Plan Required</p>
            <p className="text-sm text-amber-700 mt-0.5">
              SmartGate / Customer Wallet is available on the <strong>Professional</strong> plan.
              You're on <strong className="capitalize">{currentPlan}</strong>.
            </p>
          </div>
          <button
            onClick={() => navigate('/partners/subscription')}
            className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold text-amber-800 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors whitespace-nowrap"
            data-testid="smartgate-upgrade-btn"
          >
            <Crown className="w-3.5 h-3.5" />
            Upgrade
          </button>
        </div>
      )}

      {/* Feature Toggle */}
      <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center justify-between">
        <div>
          <p className="font-semibold text-slate-800">Enable SmartGate</p>
          <p className="text-sm text-slate-400 mt-0.5">
            When enabled, customers need points to use AI features. When off, all AI actions work normally.
          </p>
        </div>
        <Toggle
          value={settings.wallet_feature_enabled}
          onChange={v => set('wallet_feature_enabled', v)}
          disabled={planLocked}
        />
      </div>

      {/* New Customer Welcome */}
      <Section icon={Star} title="Free Points for New Customers">
        <FieldRow
          label="Welcome bonus"
          hint="Points given automatically the first time a customer uses AI features"
        >
          <NumberInput
            value={settings.welcome_bonus_points}
            onChange={v => set('welcome_bonus_points', v)}
            disabled={disabled}
            suffix="points"
            testId="welcome-bonus-input"
          />
        </FieldRow>
      </Section>

      {/* Earning Rules */}
      <Section icon={BarChart3} title="Points Earned on Purchase">
        <FieldRow
          label="Earning rate"
          hint={`Customers earn ${settings.earn_points} points for every ₹${settings.earn_amount} spent`}
        >
          <div className="flex items-center gap-3 flex-wrap">
            <NumberInput
              value={settings.earn_points}
              onChange={v => set('earn_points', v)}
              disabled={disabled}
              suffix="points"
              testId="earn-points-input"
            />
            <span className="text-sm text-slate-400">for every</span>
            <NumberInput
              value={settings.earn_amount}
              onChange={v => set('earn_amount', Math.max(1, v))}
              disabled={disabled}
              prefix="₹"
              testId="earn-amount-input"
            />
            <span className="text-sm text-slate-400">spent</span>
          </div>
          <p className="text-xs text-slate-400 mt-2">
            Example: ₹500 order = {Math.floor((500 / settings.earn_amount) * settings.earn_points)} points earned
          </p>
        </FieldRow>
      </Section>

      {/* Usage Costs */}
      <Section icon={Zap} title="Points Needed for AI Actions">
        <FieldRow label="Virtual Try-On" hint="Points deducted per TryOn request">
          <NumberInput
            value={settings.tryon_points_cost}
            onChange={v => set('tryon_points_cost', v)}
            disabled={disabled}
            suffix="points / try-on"
            testId="tryon-cost-input"
          />
        </FieldRow>
        <FieldRow label="Moments" hint="Points deducted per Moments image">
          <NumberInput
            value={settings.moments_points_cost}
            onChange={v => set('moments_points_cost', v)}
            disabled={disabled}
            suffix="points / image"
            testId="moments-cost-input"
          />
        </FieldRow>
        <FieldRow label="Size Recommendation" hint="Points deducted per size check">
          <NumberInput
            value={settings.size_recommendation_points_cost}
            onChange={v => set('size_recommendation_points_cost', v)}
            disabled={disabled}
            suffix="points / check"
            testId="size-rec-cost-input"
          />
        </FieldRow>
      </Section>

      {/* Balance Rules */}
      <Section icon={Shield} title="When Points Run Out">
        <FieldRow
          label="Block usage"
          hint="Prevent customers from using AI features when they have no points"
        >
          <Toggle
            value={settings.block_when_insufficient_balance}
            onChange={v => set('block_when_insufficient_balance', v)}
            disabled={disabled}
          />
        </FieldRow>
        <FieldRow
          label="Allow negative balance"
          hint="Let customers go negative (e.g. for grace period). Use carefully."
        >
          <div className="space-y-2">
            <Toggle
              value={settings.allow_negative_balance}
              onChange={v => set('allow_negative_balance', v)}
              disabled={disabled}
            />
            {settings.allow_negative_balance && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Limit:</span>
                <NumberInput
                  value={settings.negative_balance_limit}
                  onChange={v => set('negative_balance_limit', v)}
                  disabled={disabled}
                  suffix="pts below zero"
                  testId="negative-balance-limit-input"
                />
              </div>
            )}
          </div>
        </FieldRow>
      </Section>

      {/* Auto Credit from Orders */}
      <Section icon={Settings} title="Auto-Credit Points from Store Orders">
        <FieldRow
          label="Auto-credit from orders"
          hint="Automatically award purchase points when an order is paid"
        >
          <Toggle
            value={settings.auto_credit_from_orders_enabled}
            onChange={v => set('auto_credit_from_orders_enabled', v)}
            disabled={disabled}
          />
        </FieldRow>

        {settings.auto_credit_from_orders_enabled && (
          <>
            <FieldRow label="Order source" hint="Platform that sends order events">
              <select
                value={settings.order_event_source}
                onChange={e => set('order_event_source', e.target.value)}
                disabled={disabled}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="shopify">Shopify</option>
                <option value="manual">Manual API</option>
                <option value="custom">Custom</option>
              </select>
            </FieldRow>

            {settings.order_event_source === 'shopify' && (
              <>
                <FieldRow label="Your Shopify store domain" hint="e.g. my-store.myshopify.com">
                  <input
                    type="text"
                    value={settings.shopify_shop_domain}
                    onChange={e => set('shopify_shop_domain', e.target.value)}
                    disabled={disabled}
                    placeholder="my-store.myshopify.com"
                    data-testid="shopify-domain-input"
                    className="w-72 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </FieldRow>
                <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                  <p className="text-xs font-semibold text-slate-600 flex items-center gap-1">
                    <Info className="w-3 h-3" /> Shopify Webhook Setup
                  </p>
                  <p className="text-xs text-slate-500">
                    In your Shopify Admin, go to <strong>Settings → Notifications → Webhooks</strong> and add:
                  </p>
                  <p className="text-xs text-slate-500">Event: <strong>Order payment</strong></p>
                  <p className="text-xs font-mono bg-white border border-slate-200 rounded px-2 py-1 break-all select-all text-slate-700">
                    {window.location.origin}/api/smartgate/webhook/shopify/YOUR_PARTNER_ID/orders-paid
                  </p>
                  <p className="text-xs text-slate-400">Replace YOUR_PARTNER_ID with your actual Partner ID from your profile.</p>
                </div>
              </>
            )}
          </>
        )}
      </Section>

      {/* Preview Panel */}
      {!planLocked && settings.wallet_feature_enabled && (
        <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl p-5 text-white">
          <p className="font-semibold text-blue-100 text-sm mb-3">How it works for your customers</p>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold">{settings.welcome_bonus_points}</p>
              <p className="text-xs text-blue-200 mt-1">free points on signup</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{settings.tryon_points_cost}</p>
              <p className="text-xs text-blue-200 mt-1">points per try-on</p>
            </div>
            <div>
              <p className="text-2xl font-bold">{settings.earn_points}</p>
              <p className="text-xs text-blue-200 mt-1">points per ₹{settings.earn_amount} spent</p>
            </div>
          </div>
          <p className="text-xs text-blue-200 mt-3 text-center">
            New customers can do {settings.tryon_points_cost > 0 ? Math.floor(settings.welcome_bonus_points / settings.tryon_points_cost) : '∞'} free try-ons with the welcome bonus
          </p>
        </div>
      )}

      {/* Save button */}
      {!planLocked && (
        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="smartgate-save-btn"
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      )}
    </div>
  );
}
